// Phase 40 — Public Sample Workflows.
// Uses ONLY existing completed examples. Nothing is invented or rewritten.
//   - Flagship: EXAMPLE_SESSIONS[0] (Deep Synthesis, "combine" mode)
//   - Cross-Fusion: LIVE_FUSION_EXAMPLE
//   - Quick Reply: two entries from QUICK_REPLY_SAMPLES
// All content is frozen static data from /app/frontend/src/data/*.
import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Brain,
  ChevronDown,
  ChevronUp,
  Download,
  FileText,
  GitBranch,
  GitMerge,
  MessageSquare,
  ShieldCheck,
  Sparkles,
  Terminal,
} from "lucide-react";
import LIVE_FUSION_EXAMPLE from "../data/liveFusionExample";
import QUICK_REPLY_SAMPLES from "../data/quickReplySamples";
import exampleResults from "../data/exampleResults.json";

function SampleLabel() {
  return (
    <span className="inline-flex items-center gap-1 text-[9.5px] font-mono uppercase tracking-[0.2em] border border-amber-400/50 bg-amber-400/10 text-amber-200 px-1.5 py-0.5">
      SAMPLE OUTPUT
    </span>
  );
}

// ---- Flagship Deep Synthesis (LiveResults-style: full output + diagram + PDF) ----
function FlagshipSection({ section }) {
  const { title, kind, items = [] } = section;
  return (
    <div className="border border-[#27272A] bg-[#0A0A0A]">
      <div className="px-3 py-2 border-b border-[#27272A] flex items-center justify-between">
        <span className="text-[11px] font-mono uppercase tracking-wider text-white">{title}</span>
        <span className="text-[10px] font-mono text-zinc-600 uppercase">{kind}</span>
      </div>
      <div className="p-3 space-y-2 text-[12.5px]">
        {kind === "plaque" && (
          <pre className="text-[10px] leading-snug font-mono text-emerald-300 bg-[#050505] border border-emerald-400/20 p-2 overflow-x-auto whitespace-pre">
            {items.map((it) => (typeof it === "string" ? it : it.detail || "")).join("\n")}
          </pre>
        )}
        {(kind === "list" || kind === "expand") &&
          items.map((it, i) => (
            <div key={i} className="flex gap-2">
              <span className="text-amber-400 font-mono">·</span>
              <div>
                {typeof it === "string" ? (
                  <span className="text-zinc-200">{it}</span>
                ) : (
                  <>
                    <span className="text-white font-mono mr-2">{it.label}</span>
                    <span className="text-zinc-400">{it.detail}</span>
                  </>
                )}
              </div>
            </div>
          ))}
        {kind === "plain" &&
          items.map((it, i) => (
            <p key={i} className="text-zinc-200 leading-relaxed">
              {typeof it === "string" ? it : it.detail || ""}
            </p>
          ))}
      </div>
    </div>
  );
}

function FlagshipDeepSynthesis() {
  const [open, setOpen] = useState(false);
  const ex = (exampleResults?.examples || [])[0];
  if (!ex) {
    // Fallback: leave section unchanged rather than inventing content.
    return null;
  }
  return (
    <div
      data-testid="landing-flagship-deep-synth"
      className="border-2 border-purple-400/40 bg-[#0A0A0A] p-4 sm:p-5"
    >
      <div className="flex items-center gap-2 flex-wrap">
        <span className="inline-flex items-center gap-1 text-[9.5px] font-mono uppercase tracking-[0.2em] border border-amber-400/50 bg-amber-400/10 text-amber-200 px-1.5 py-0.5">
          SAMPLE OUTPUT
        </span>
        <span className="text-[10px] font-mono uppercase tracking-[0.25em] text-purple-300">
          FLAGSHIP · DEEP_SYNTHESIS · REAL_OUTPUT · UNEDITED
        </span>
      </div>
      <h3 className="mt-3 text-xl sm:text-2xl text-white font-mono tracking-tight leading-tight">
        {ex.topic}
      </h3>

      {/* DOI integrity badge */}
      <div
        data-testid="flagship-doi-integrity"
        className="mt-3 inline-flex flex-wrap items-center gap-2 text-[10px] font-mono uppercase tracking-wider text-emerald-300 border border-emerald-400/40 bg-emerald-500/[0.05] px-3 py-1.5"
      >
        <ShieldCheck size={12} /> DOI_INTEGRITY · 11/11 DOIs VERIFIED
        <span className="text-zinc-500">·</span> 0 DUPLICATES
        <span className="text-zinc-500">·</span> 0 MISMATCHES
        <span className="text-zinc-500">·</span> 0 UNCONFIRMED
      </div>

      {/* prompt */}
      <div className="mt-3 border border-amber-400/40 bg-[#0E0E0E]">
        <div className="px-3 py-2 border-b border-amber-400/30 flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.2em]">
          <Terminal size={12} className="text-amber-300" />
          <span className="text-amber-300">USER_PROMPT</span>
        </div>
        <p className="p-3 text-[13px] text-zinc-100 leading-relaxed font-mono">
          {ex.prompt}
        </p>
      </div>

      {/* Three-pane preview: reply · diagram · pdf */}
      <div className="mt-4 grid grid-cols-1 xl:grid-cols-3 gap-3">
        <div className="border border-[#27272A] bg-[#0A0A0A] flex flex-col">
          <div className="px-3 py-2 border-b border-[#27272A] flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-400">
            <MessageSquare size={11} /> 1 · BOT_REPLY
          </div>
          <div className="p-2 space-y-2 overflow-auto" style={{ maxHeight: 420 }}>
            {(open ? ex.sections : ex.sections.slice(0, 2)).map((s, i) => (
              <FlagshipSection key={i} section={s} />
            ))}
          </div>
        </div>

        <div className="border border-[#27272A] bg-[#0A0A0A] flex flex-col">
          <div className="px-3 py-2 border-b border-[#27272A] flex items-center justify-between">
            <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-400 inline-flex items-center gap-2">
              <GitBranch size={11} /> 2 · STRUCTURAL_DIAGRAM
            </span>
            <a
              data-testid="flagship-download-svg"
              href={ex.svg}
              download
              className="inline-flex items-center gap-1 text-[10px] font-mono text-zinc-400 hover:text-white"
            >
              <Download size={11} /> SVG
            </a>
          </div>
          <div className="p-2 bg-white flex-1 flex items-start justify-center overflow-auto" style={{ maxHeight: 420 }}>
            <img
              src={ex.svg}
              alt="Structural synthesis diagram"
              className="w-full object-contain"
            />
          </div>
        </div>

        <div className="border border-[#27272A] bg-[#0A0A0A] flex flex-col">
          <div className="px-3 py-2 border-b border-[#27272A] flex items-center justify-between">
            <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-400 inline-flex items-center gap-2">
              <FileText size={11} /> 3 · EXECUTIVE_SUMMARY_PDF
            </span>
            <a
              data-testid="flagship-download-pdf"
              href={ex.pdf}
              download
              className="inline-flex items-center gap-1 text-[10px] font-mono text-amber-300 hover:text-amber-200"
            >
              <Download size={11} /> PDF
            </a>
          </div>
          <iframe
            title="Executive Summary PDF"
            src={ex.pdf}
            className="w-full bg-white border-0 flex-1"
            style={{ minHeight: 420 }}
          />
        </div>
      </div>

      {/* Expand for the rest of the sections */}
      {ex.sections.length > 2 && (
        <button
          data-testid="flagship-expand"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className="mt-4 inline-flex items-center gap-2 h-9 px-3 border border-[#27272A] text-zinc-200 text-[11px] font-mono uppercase tracking-wider hover:bg-[#141414] hover:text-white"
        >
          {open ? (
            <>
              <ChevronUp size={12} /> HIDE FULL SYNTHESIS
            </>
          ) : (
            <>
              <ChevronDown size={12} /> READ FULL SYNTHESIS
            </>
          )}
        </button>
      )}
    </div>
  );
}

// ---- Smaller Cross-Fusion card ----
function CrossFusionSmall() {
  const ex = LIVE_FUSION_EXAMPLE;
  const fc = ex.fused_concept;
  return (
    <div
      data-testid="landing-sample-cross-fusion"
      className="border border-[#27272A] bg-[#0A0A0A] p-4"
    >
      <div className="flex items-center gap-2">
        <SampleLabel />
        <span className="text-[9.5px] font-mono uppercase tracking-wider text-zinc-500">
          CROSS-FUSION
        </span>
      </div>
      <h4 className="mt-2 text-[15px] text-white leading-snug font-mono tracking-tight">
        {fc.title}
      </h4>
      <p className="mt-1 text-[12.5px] text-zinc-300 leading-relaxed">
        {fc.one_liner}
      </p>
      <p className="mt-2 text-[12px] text-zinc-400 leading-relaxed">
        {fc.combined_use_case}
      </p>
      <div className="mt-3 text-[10px] font-mono uppercase tracking-wider text-zinc-500">
        FUSED FROM:{" "}
        {ex.input_platforms.map((p) => p.title.split(" for ")[0]).join(" × ")}
      </div>
    </div>
  );
}

// ---- Smaller Quick Reply card ----
function QuickReplySmall({ s }) {
  const a = s.answer;
  return (
    <div
      data-testid={`landing-sample-quick-reply-${s.id}`}
      className="border border-[#27272A] bg-[#0A0A0A] p-4"
    >
      <div className="flex items-center gap-2">
        <SampleLabel />
        <span className="text-[9.5px] font-mono uppercase tracking-wider text-zinc-500">
          QUICK REPLY · {s.kind.replace("_", " ")}
        </span>
      </div>
      <p className="mt-2 text-[12px] text-zinc-400 leading-snug">
        <span className="text-zinc-500 font-mono uppercase text-[9.5px] mr-1">
          Q ·
        </span>
        {s.prompt}
      </p>
      <p className="mt-2 text-[13px] text-white leading-snug">{a.headline}</p>
      {(a.why_now || a.state_of_the_field) && (
        <p className="mt-2 text-[12px] text-zinc-300 leading-relaxed">
          {a.why_now || a.state_of_the_field}
        </p>
      )}
    </div>
  );
}

export default function LandingSampleWorkflows() {
  return (
    <section
      data-testid="landing-sample-workflows"
      className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-14"
    >
      <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-emerald-300">
        SAMPLE WORKFLOWS · COMPLETED · PUBLIC
      </div>
      <h2 className="mt-2 text-2xl sm:text-3xl font-mono tracking-tight text-white leading-tight">
        What MindStein produces
      </h2>
      <p className="mt-2 text-[13.5px] text-zinc-400 leading-relaxed max-w-3xl">
        Three completed workflow examples. Every card below is a static
        Sample Output — none of it runs live from this page. Live analysis
        opens through controlled early access.
      </p>

      {/* Flagship */}
      <div className="mt-6">
        <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.25em] text-purple-300 mb-2">
          <Brain size={11} /> DEEP_SYNTHESIS · FLAGSHIP
        </div>
        <FlagshipDeepSynthesis />
      </div>

      {/* Smaller Cross-Fusion */}
      <div className="mt-8">
        <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.25em] text-emerald-300 mb-2">
          <GitMerge size={11} /> CROSS-FUSION
        </div>
        <CrossFusionSmall />
      </div>

      {/* Smaller Quick Replies */}
      <div className="mt-8">
        <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.25em] text-emerald-300 mb-2">
          <Sparkles size={11} /> QUICK_REPLY
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {QUICK_REPLY_SAMPLES.map((s) => (
            <QuickReplySmall key={s.id} s={s} />
          ))}
        </div>
      </div>

      {/* Health/medical footnote */}
      <p className="mt-8 text-[11.5px] text-zinc-500 leading-snug max-w-3xl">
        For health or medical topics: MindStein supports research workflow
        exploration only. It does not provide clinical guidance and does not
        replace expert review.
      </p>

      {/* Concise Join the Whitelist card */}
      <div
        data-testid="landing-post-sample-whitelist"
        className="mt-8 border border-emerald-400/30 bg-emerald-500/[0.04] p-4 sm:p-5"
      >
        <div className="text-[10px] uppercase tracking-[0.25em] text-emerald-300">
          JOIN THE WHITELIST
        </div>
        <p className="mt-1 text-[13px] text-zinc-100 leading-relaxed max-w-3xl">
          Verify your email to unlock the Intelligence Layer, the MindStein
          Workspace Preview, additional example material, and controlled
          early-access updates. No card. No credits.
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Link
            to="/register"
            data-testid="landing-sample-whitelist-cta"
            className="h-9 px-3 inline-flex items-center gap-2 border border-emerald-400 bg-emerald-400 text-black text-[11px] uppercase tracking-wider hover:bg-emerald-300"
          >
            Join the whitelist <ArrowRight size={13} />
          </Link>
        </div>
      </div>
    </section>
  );
}
